import styled from "styled-components";

interface SelectedProps {
  $selected: boolean;
}

interface FilledProps {
  /** False while the chosen audience still names nobody. */
  $filled: boolean;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${(props) => props.theme.brand.line};
  border-radius: 8px;
  background: #fafbfd;
`;

export const Legend = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.brand.soft};
`;

/** The two ways of choosing an audience, side by side so they read as a choice. */
export const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Option = styled.label<SelectedProps>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 12px 11px 6px;
  border: 1px solid ${(props) => (props.$selected ? props.theme.brand.navyMed : props.theme.brand.line)};
  border-radius: 8px;
  background: ${(props) => (props.$selected ? "#eef4fb" : "#ffffff")};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${(props) => (props.$selected ? props.theme.brand.navyMed : "#c0cfe0")};
  }
`;

export const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-top: 7px;
`;

export const OptionTitle = styled.span<SelectedProps>`
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => (props.$selected ? props.theme.brand.navy : props.theme.brand.ink)};
`;

export const OptionHint = styled.span`
  font-size: 11.5px;
  line-height: 1.35;
  color: ${(props) => props.theme.brand.muted};
`;

/**
 * One person in the search results: the name on top, the email and the customer
 * underneath. Homonyms are common across customers, so the name alone is not
 * enough to tell two people apart.
 */
export const Recipient = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

export const RecipientName = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.brand.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RecipientDetails = styled.span`
  display: flex;
  flex-direction: row;
  gap: 6px;
  font-size: 11.5px;
  color: ${(props) => props.theme.brand.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RecipientTenant = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.brand.navyMed};
`;

export const Pickers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 2px;
`;

/** One picker plus the line explaining what picking there actually does. */
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Hint = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.brand.soft};
`;

/**
 * Live answer to "who gets this?". Turns amber while the chosen audience has
 * no recipients yet, which is exactly what the server would reject on save.
 */
export const Summary = styled.div<FilledProps>`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 5px;
  padding: 9px 12px;
  border-radius: 6px;
  background: ${(props) => (props.$filled ? "#eef4fb" : "#fdf3e6")};
  font-size: 12px;
  color: ${(props) => (props.$filled ? props.theme.brand.navy : props.theme.brand.orange)};
`;

export const SummaryLabel = styled.span`
  font-weight: 600;
`;
